/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Dashboard Filter State
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Reactive filter state shared by a `<DashboardFilterBar>` and the data
/// fetchers underneath it. Each page configures which filters are enabled —
/// `useDashboardFilters({ dateRange: true, warehouses: true })` shows just
/// those two. The bar reads `enabled` to decide what to render; the cache
/// keys downstream incorporate filter values so switching back to a
/// previously-viewed range hits cache instantly.
///
/// Filter types currently implemented:
///   - dateRange   — start/end timestamps with preset shortcuts
///   - warehouses  — multi-select string array (UI present, query plumbing
///                   is page-specific; opt-in)
///   - users       — multi-select string array (same)
///   - roles       — multi-select string array (same)
///
/// Adding a new filter type: add it to FilterEnabled, FilterState, the
/// constructor defaults, and (where applicable) a serialization key in
/// `filterCacheKey`. Render the matching control inside DashboardFilterBar.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateRange {
  /** Inclusive start of the range. */
  start: Date
  /** Exclusive end of the range (e.g. for `START_TIME < end`). */
  end: Date
  /** Stable identifier for the active preset, or 'custom' for a custom
   *  range. Drives the picker's "selected preset" highlighting and the
   *  display label. */
  presetId: DateRangePresetId
}

export type DateRangePresetId =
  | 'today'
  | 'yesterday'
  | 'last_7d'
  | 'last_14d'
  | 'last_30d'
  | 'last_90d'
  | 'last_1y'
  | 'this_month'
  | 'last_month'
  | 'custom'

export interface FilterEnabled {
  dateRange?: boolean
  warehouses?: boolean
  users?: boolean
  roles?: boolean
}

export interface FilterState {
  enabled: Required<FilterEnabled>
  dateRange: DateRange
  warehouses: string[]
  users: string[]
  roles: string[]
}

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

interface PresetDef {
  id: DateRangePresetId
  label: string
  /** Build the {start, end} pair for this preset, evaluated lazily so we
   *  always anchor at "now" rather than the moment the page loaded. */
  build: () => Omit<DateRange, 'presetId'>
}

function startOfDay(d: Date): Date {
  const c = new Date(d); c.setHours(0, 0, 0, 0); return c
}
function startOfDayDaysAgo(n: number): Date {
  return startOfDay(new Date(Date.now() - n * 86_400_000))
}
function endOfDay(d: Date): Date {
  const c = new Date(d); c.setHours(23, 59, 59, 999); return c
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

export const DATE_RANGE_PRESETS: PresetDef[] = [
  {
    id: 'today',
    label: 'Today',
    build: () => ({ start: startOfDay(new Date()), end: new Date() }),
  },
  {
    id: 'yesterday',
    label: 'Yesterday',
    build: () => {
      const start = startOfDayDaysAgo(1)
      const end = endOfDay(new Date(Date.now() - 86_400_000))
      return { start, end }
    },
  },
  { id: 'last_7d',  label: 'Last 7 days',  build: () => ({ start: startOfDayDaysAgo(7),  end: new Date() }) },
  { id: 'last_14d', label: 'Last 14 days', build: () => ({ start: startOfDayDaysAgo(14), end: new Date() }) },
  { id: 'last_30d', label: 'Last 30 days', build: () => ({ start: startOfDayDaysAgo(30), end: new Date() }) },
  { id: 'last_90d', label: 'Last 90 days', build: () => ({ start: startOfDayDaysAgo(90), end: new Date() }) },
  { id: 'last_1y',  label: 'Last year',    build: () => ({ start: startOfDayDaysAgo(365), end: new Date() }) },
  {
    id: 'this_month',
    label: 'This month',
    build: () => ({ start: startOfMonth(new Date()), end: new Date() }),
  },
  {
    id: 'last_month',
    label: 'Last month',
    build: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = startOfMonth(now)
      return { start, end }
    },
  },
]

const PRESET_BY_ID = Object.fromEntries(DATE_RANGE_PRESETS.map(p => [p.id, p]))

/** Build a DateRange from a preset id. Falls back to last_30d if unknown. */
export function buildPresetRange(id: DateRangePresetId): DateRange {
  const def = PRESET_BY_ID[id] || PRESET_BY_ID.last_30d
  return { ...def.build(), presetId: def.id as DateRangePresetId }
}

/** Display label for a date range. Presets show their friendly label
 *  (e.g. "Last 30 days"); custom ranges show "MMM D – MMM D" so the
 *  filter bar's chip stays scannable. */
export function formatDateRangeLabel(range: DateRange): string {
  if (range.presetId !== 'custom') {
    return PRESET_BY_ID[range.presetId]?.label ?? 'Custom'
  }
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return `${fmt(range.start)} – ${fmt(range.end)}`
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export interface UseDashboardFiltersOptions extends FilterEnabled {
  /** Default preset for the date range. Ignored if `dateRange: false`. */
  defaultDateRange?: DateRangePresetId
}

export function useDashboardFilters(opts: UseDashboardFiltersOptions = {}) {
  const enabled: Required<FilterEnabled> = {
    dateRange: opts.dateRange ?? false,
    warehouses: opts.warehouses ?? false,
    users: opts.users ?? false,
    roles: opts.roles ?? false,
  }

  const state = reactive<FilterState>({
    enabled,
    dateRange: buildPresetRange(opts.defaultDateRange ?? 'last_30d'),
    warehouses: [],
    users: [],
    roles: [],
  })

  /** Reset all enabled filters back to their defaults. */
  function reset() {
    if (enabled.dateRange) state.dateRange = buildPresetRange(opts.defaultDateRange ?? 'last_30d')
    if (enabled.warehouses) state.warehouses = []
    if (enabled.users) state.users = []
    if (enabled.roles) state.roles = []
  }

  /** Whether any enabled filter has a non-default value. */
  const isDirty = computed(() => {
    if (enabled.dateRange && state.dateRange.presetId !== (opts.defaultDateRange ?? 'last_30d')) return true
    if (enabled.warehouses && state.warehouses.length) return true
    if (enabled.users && state.users.length) return true
    if (enabled.roles && state.roles.length) return true
    return false
  })

  return { state, reset, isDirty }
}

// ---------------------------------------------------------------------------
// Helpers — used by data fetchers to incorporate filters into queries + cache
// ---------------------------------------------------------------------------

/** Format a Date as a Snowflake-friendly ISO string (UTC, second-precision).
 *  Snowflake accepts these directly in TIMESTAMP literals via `TO_TIMESTAMP`
 *  or in BETWEEN clauses with implicit casting. */
export function toSnowflakeTimestamp(d: Date): string {
  // YYYY-MM-DD HH:MM:SS — drop ms + timezone. Snowflake interprets these
  // as TIMESTAMP_NTZ which matches what ACCOUNT_USAGE.* views expose.
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '')
}

/** Stable, short cache key fragment for a date range. Used by the
 *  dashboard pages to scope SWR caches per range — switching from
 *  Last 30d to Last 7d shouldn't show stale "Last 30d" data. */
export function dateRangeCacheKey(range: DateRange | null | undefined): string {
  if (!range) return 'norange'
  if (range.presetId !== 'custom') return range.presetId
  // For custom, hash the start/end at day precision (custom-by-day is
  // the granularity our pickers offer; finer would just bloat the key).
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return `custom_${fmt(range.start)}_${fmt(range.end)}`
}

/** Convenience for fetchers that still take an integer days-back rather
 *  than a {start, end}. Computes the gap; falls back to 30 if the range
 *  is missing or inverted. */
export function rangeToDaysBack(range: DateRange | null | undefined): number {
  if (!range) return 30
  const ms = range.end.getTime() - range.start.getTime()
  if (ms <= 0) return 30
  return Math.max(1, Math.ceil(ms / 86_400_000))
}
